import { useEffect, useRef } from "react"
import { getCableConsumer, subscribeCableChannel } from "@/lib/cable-consumer"
import { fetchTaskNotificationsSummary } from "@/lib/task-notifications-api"
import { useTaskNotificationsStore } from "@/stores/task-notifications-store"
import type { TaskNotificationsSummary } from "@/types/task-notifications"

const REFETCH_DELAY_MS = 500

export function useTaskNotifications(seed: TaskNotificationsSummary | null, companyId: number | null): void {
  const enabled = seed != null && companyId != null
  const seedKey = seed ? `${seed.company_id}:${seed.at}` : ""
  const seedRef = useRef(seed)
  const companyIdRef = useRef(companyId)
  const timerRef = useRef<number | null>(null)
  const abortRef = useRef<AbortController | null>(null)
  const pendingRef = useRef(false)
  seedRef.current = seed
  companyIdRef.current = companyId

  useEffect(() => {
    if (seedRef.current) useTaskNotificationsStore.getState().accept(seedRef.current)
  }, [seedKey])

  useEffect(() => {
    if (!enabled) return

    const refetch = () => {
      abortRef.current?.abort()
      const controller = new AbortController()
      abortRef.current = controller
      fetchTaskNotificationsSummary(controller.signal)
        .then(({ summary }) => {
          if (controller.signal.aborted || summary.company_id !== companyIdRef.current) return
          useTaskNotificationsStore.getState().refreshed(summary)
        })
        .catch(() => {})
        .finally(() => {
          if (abortRef.current === controller) abortRef.current = null
        })
    }

    const schedule = () => {
      if (document.hidden) {
        pendingRef.current = true
        return
      }
      pendingRef.current = false
      if (timerRef.current !== null) return
      timerRef.current = window.setTimeout(() => {
        timerRef.current = null
        refetch()
      }, REFETCH_DELAY_MS)
    }

    const onVisibilityChange = () => {
      if (!document.hidden && pendingRef.current) schedule()
    }
    document.addEventListener("visibilitychange", onVisibilityChange)

    const unsubscribe = subscribeCableChannel(
      getCableConsumer(),
      { channel: "TaskNotificationsChannel" },
      {
        connected() {
          schedule()
        },
        received(data: unknown) {
          if (!data || typeof data !== "object") return
          if ((data as { type?: string }).type !== "task_notifications_changed") return
          schedule()
        },
      },
    )

    return () => {
      unsubscribe()
      document.removeEventListener("visibilitychange", onVisibilityChange)
      if (timerRef.current !== null) {
        window.clearTimeout(timerRef.current)
        timerRef.current = null
      }
      abortRef.current?.abort()
      abortRef.current = null
      pendingRef.current = false
    }
  }, [enabled])
}
